import type { CreateFormFromTemplateInput, FormTemplateContract } from '@/contracts/form-templates'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { FileText, Sparkles } from 'lucide-react'
import { useMemo, useState } from 'react'

type FormTemplatePickerDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  templates: FormTemplateContract[]
  isCreating?: boolean
  onCreateForm: (
    template: FormTemplateContract,
    input: CreateFormFromTemplateInput,
  ) => Promise<void> | void
}

export function FormTemplatePickerDialog({
  open,
  onOpenChange,
  templates,
  isCreating = false,
  onCreateForm,
}: FormTemplatePickerDialogProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('')
  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? templates[0],
    [selectedTemplateId, templates],
  )
  const [title, setTitle] = useState('')
  const resolvedTitle = title.trim() || (selectedTemplate ? `${selectedTemplate.title}（新問卷）` : '')

  async function handleCreate() {
    if (!selectedTemplate) return
    await onCreateForm(selectedTemplate, {
      title: resolvedTitle,
      description: selectedTemplate.description,
    })
    setTitle('')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl overflow-hidden p-0">
        <DialogHeader className="border-b border-slate-200 px-6 py-5">
          <DialogTitle>使用問卷範本</DialogTitle>
          <DialogDescription>
            選擇範本後會建立一份新的草稿問卷，原範本內容不會被修改。
          </DialogDescription>
        </DialogHeader>
        <div className="grid min-h-[440px] md:grid-cols-[minmax(0,1fr)_300px]">
          <ScrollArea className="min-h-0 border-r border-slate-200" viewportClassName="p-4">
            <div className="grid gap-3">
              {templates.map((template) => {
                const active = selectedTemplate?.id === template.id
                return (
                  <button
                    key={template.id}
                    type="button"
                    className={cn(
                      'grid gap-3 rounded-lg border p-4 text-left transition-colors',
                      active
                        ? 'border-indigo-300 bg-indigo-50 text-indigo-950'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50',
                    )}
                    onClick={() => {
                      setSelectedTemplateId(template.id)
                      setTitle(`${template.title}（新問卷）`)
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={cn(
                          'grid size-10 shrink-0 place-items-center rounded-lg',
                          active ? 'bg-white text-indigo-700' : 'bg-slate-100 text-slate-500',
                        )}
                      >
                        {template.isSystem ? (
                          <Sparkles className="size-5" />
                        ) : (
                          <FileText className="size-5" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-semibold">{template.title}</h3>
                          {template.isSystem ? <Badge variant="secondary">固定範本</Badge> : null}
                        </div>
                        <p className="mt-1 line-clamp-2 text-sm leading-6 text-slate-500">
                          {template.description || '尚未填寫範本描述。'}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                      <Badge variant="outline">{template.questionCount} 題</Badge>
                      <Badge variant="outline">{template.groupCount} 題組</Badge>
                      <Badge variant="outline">{template.category}</Badge>
                    </div>
                  </button>
                )
              })}
              {templates.length === 0 ? (
                <div className="grid min-h-72 place-items-center rounded-lg border border-dashed border-slate-300 p-6 text-center">
                  <div>
                    <FileText className="mx-auto size-8 text-slate-400" />
                    <h3 className="mt-3 font-semibold text-slate-950">目前沒有可用範本</h3>
                    <p className="mt-1 text-sm text-slate-500">
                      可先從問卷編輯器把現有問卷存成問卷範本。
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </ScrollArea>
          <div className="flex min-h-0 flex-col bg-slate-50 p-5">
            <div className="min-h-0 flex-1">
              <h3 className="font-semibold text-slate-950">建立新問卷</h3>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                新問卷會以草稿建立，題目、題組與已保存的邏輯會從範本複製。
              </p>
              <div className="mt-5">
                <Label htmlFor="template-new-form-title">新問卷名稱</Label>
                <Input
                  id="template-new-form-title"
                  className="mt-2"
                  value={title}
                  placeholder={selectedTemplate ? `${selectedTemplate.title}（新問卷）` : '新問卷'}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </div>
              {selectedTemplate ? (
                <div className="mt-5 rounded-lg border border-slate-200 bg-white p-4 text-sm">
                  <div className="font-medium text-slate-950">{selectedTemplate.title}</div>
                  <div className="mt-2 text-slate-500">
                    {selectedTemplate.questionCount} 題、{selectedTemplate.groupCount} 題組
                  </div>
                </div>
              ) : null}
            </div>
            <div className="mt-5 flex justify-end gap-2 border-t border-slate-200 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button
                type="button"
                onClick={() => void handleCreate()}
                disabled={!selectedTemplate || isCreating}
              >
                {isCreating ? '建立中' : '建立問卷'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
